import { Sort } from '@angular/material/sort';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
  ViewContainerRef,
  AfterViewInit,
  OnChanges,
  ContentChild,
  TemplateRef
} from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { MatTableDataSource } from '@angular/material/table';

import { withLatestFrom } from 'rxjs';

import { ISelectionState, ITableColumnConfig, ITableSort, ITableStyleType } from './interfaces';
import { ScrollListContainerBaseComponent } from '../scroll-list-container/scroll-list-container-base.component';
import { TableVirtualScrollDataSource } from './vs-for-table/table-data-source';

@Component({
  selector: 'vi-ui-table',
  templateUrl: `./table.component.html`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./table.component.scss'],
  standalone: false
})
export class TableComponent<T> extends ScrollListContainerBaseComponent<T> implements OnInit, OnChanges, AfterViewInit {
  @ViewChildren('componentCell', { read: ViewContainerRef }) containers: QueryList<ViewContainerRef>;

  // Templates
  @ContentChild('emptyStateTemplate') emptyStateTemplate: TemplateRef<unknown>;
  @ContentChild('loadingTemplate') loadingTemplate: TemplateRef<unknown>;

  // VirtualScrollListContainerBaseComponent properties
  @Input() public items: T[];
  @Input() public isLoading: boolean = false;
  @Input() public isVirtualScrollActive: boolean = false;
  @Input() public itemSize: number;
  @Input() public hasMore: boolean = true;
  @Input() public autoFillViewport: boolean = false;

  @Output() public loadMore: EventEmitter<void> = new EventEmitter<void>();

  @Input() public columnsConfig: ITableColumnConfig[];
  @Input() public styleType: ITableStyleType = ITableStyleType.DEFAULT;
  @Input() public selectable = false;
  @Input() public allowMultiSelect = true;
  @Input() public initialSelection = [];

  /**
   * when this number of items is left to the bottom of the list, the loadMore event will be emitted
   * @default 5
   */
  @Input() public loadMoreItemsThreshold = 5;

  @Output() public itemSelected = new EventEmitter<ISelectionState<T>>();
  @Output() public allItemSelected = new EventEmitter<boolean>();
  @Output() public sort = new EventEmitter<ITableSort>();

  public selection: SelectionModel<T>;
  public dataSource: MatTableDataSource<T>;
  public ITableStyleType = ITableStyleType;
  // Please avoid changing this to 'select' to prevent Material from applying styles that do not align with our design.
  public SELECT_COLUMN = 'select_column';
  public showHeaderSelection = false;
  public headerRowHovered = false;

  private readonly ROW_HEIGHTS = {
    [ITableStyleType.DEFAULT]: 43,
    [ITableStyleType.COMPACT]: 33
  };

  private hoveredRowIndex: number = -1;
  private viewportResizeObserver: ResizeObserver;
  private observerFrameId: number;

  constructor(private cdr: ChangeDetectorRef) {
    super();
  }

  public get rowHeight() {
    return this.ROW_HEIGHTS[this.styleType];
  }

  public get displayedColumns() {
    const itemsColumns = this.columnsConfig.map(column => column.key);
    if (this.selectable) {
      return [this.SELECT_COLUMN, ...itemsColumns];
    }
    return itemsColumns;
  }

  public ngOnInit() {
    this.initializeDataSource();
    // Set the item size based on the style type - this is only used this way in the table component.
    // The rest of our components passing the itemSize property directly.
    this.itemSize = this.rowHeight;

    if (this.selectable) {
      this.selection = new SelectionModel<T>(this.allowMultiSelect, this.initialSelection);
    }
  }

  public override ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);

    if (this.dataSource && changes.items) {
      this.dataSource.data = this.items;
      if (this.selectable) {
        this.selection = new SelectionModel<T>(this.allowMultiSelect, this.initialSelection);
      }

      this.cdr.detectChanges();
      this.setupVirtualScroll();
      this.scheduleLoadMoreObserverRefresh();
    }

    if (changes.isLoading || changes.hasMore || changes.autoFillViewport) {
      this.scheduleLoadMoreObserverRefresh();
    }
  }

  public override ngAfterViewInit() {
    super.ngAfterViewInit();
    this.setupVirtualScroll();
    this.scheduleLoadMoreObserverRefresh();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.viewportResizeObserver?.disconnect();
    cancelAnimationFrame(this.observerFrameId);
  }

  public isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.items.length;
    return numSelected == numRows;
  }

  public toggleAllRows() {
    const isAllSelected = this.isAllSelected();
    isAllSelected ? this.selection.clear() : this.items.forEach(item => this.selection.select(item));
    this.allItemSelected.emit(!isAllSelected);
  }

  public toggleRow(item: T) {
    this.selection.toggle(item);
    this.itemSelected.emit({ item, isSelected: this.selection.isSelected(item) });
  }

  public sortData(sort: Sort) {
    const column = this.columnsConfig[sort.active];
    if (!column.sortMethod) {
      this.sort.emit({ key: column.sortKey || column.key, direction: sort.direction });
      return;
    }
    const data = this.items.slice();
    if (!column.sortable || !sort.active || sort.direction === '') {
      return;
    }
    this.dataSource.data = data.sort((item1, item2) => {
      const isAsc = sort.direction === 'asc';
      const comparisonResult = column.sortMethod(item1, item2);
      return comparisonResult * (isAsc ? 1 : -1);
    });
  }

  public trackByIndex(index: number): number {
    return index;
  }

  public isRowHovered(index: number): boolean {
    return this.hoveredRowIndex === index;
  }

  public setItemHovered(index: number, isHovered: boolean) {
    this.hoveredRowIndex = isHovered ? index : -1;
  }

  public setHeaderHovered(isHovered: boolean) {
    this.headerRowHovered = isHovered;
    this.cdr.detectChanges();
  }

  public onLoadMore(event?: Event): void {
    if (this.hasMore && !this.isLoading) {
      this.loadMore.emit();
    }
  }

  public override initViewportListener() {
    super.initViewportListener(this.rowHeight * this.loadMoreItemsThreshold);
    if (!this.autoFillViewport || !this.viewport || this.viewportResizeObserver) {
      return;
    }

    this.viewportResizeObserver = new ResizeObserver(() => this.scheduleLoadMoreObserverRefresh());
    this.viewportResizeObserver.observe(this.viewport.elementRef.nativeElement);
    this.scheduleLoadMoreObserverRefresh();
  }

  private initializeDataSource(): void {
    if (this.isVirtualScrollActive) {
      this.dataSource = new TableVirtualScrollDataSource<T>(this.items);
      return;
    }
    this.dataSource = new MatTableDataSource<T>(this.items);
  }

  private setupVirtualScroll(): void {
    if (this.isVirtualScrollActive) {
      this.containers.changes.pipe(withLatestFrom((this.dataSource as TableVirtualScrollDataSource<T>).dataOfRange$)).subscribe(([, data]) => {
        this.setCustomColumns(this.sortedContainers, data);
      });
    } else {
      // Use sortedContainers for non-virtualized tables as well to maintain consistent ordering
      this.setCustomColumns(this.sortedContainers, this.items);
    }
  }

  private scheduleLoadMoreObserverRefresh(): void {
    cancelAnimationFrame(this.observerFrameId);
    this.observerFrameId = requestAnimationFrame(() => {
      if (!this.autoFillViewport || !this.viewport || !this.hasMore || this.isLoading) {
        return;
      }

      const viewport = this.viewport.elementRef.nativeElement;
      if (viewport.scrollHeight <= viewport.clientHeight) {
        this.onLoadMore();
      }
    });
  }

  /**
   * Sets columns that show component in the cells.
   *
   * For each column that should show component, it creates an instance of the component and assigns it to the corresponding container.
   * It also sets the component's inputs based on the `componentInputs` configuration.
   */
  private setCustomColumns(containers, data) {
    let index = 0;
    this.columnsConfig.forEach(column => {
      if (!column?.component) {
        return;
      }

      const componentClass = column.component;

      data.forEach(item => {
        const container = containers[index++];
        container.clear();
        const component = container.createComponent(componentClass);
        Object.entries(column.componentInputs).forEach(([key, factory]) => {
          component.instance[key] = factory(item);
        });
      });
      this.cdr.detectChanges();
    });
  }

  /**
   * Retrieves the containers sorted by columns.
   *
   * This getter method converts the containers into an array and divides them into columns based on the number of columns containing components.
   * It then sorts the containers within each column.
   *
   * @returns An array of sorted containers.
   *
   * @remarks
   * Sorting containers is crucial for correctly mapping the containers to their respective columns.
   */
  private get sortedContainers() {
    const containersArray = this.containers.toArray();
    const numContainersInEachColumn = containersArray.length / this.columnsWithComponent;
    const containersColumns = [];
    for (let i = 0; i < this.containers.length; i += numContainersInEachColumn) {
      containersColumns.push(this.containers.toArray().slice(i, i + numContainersInEachColumn));
    }

    const sortedContainers = containersColumns.map(array => this.sortContainers(array));
    return [].concat(...sortedContainers);
  }

  private sortContainers(containers: ViewContainerRef[]) {
    // Sort containers based on their position in the DOM, This is needed when scroll up and new container is created above the existing ones
    return containers.sort((a, b) =>
      a.element.nativeElement.compareDocumentPosition(b.element.nativeElement) === Node.DOCUMENT_POSITION_PRECEDING ? 1 : -1
    );
  }

  private get columnsWithComponent() {
    return this.columnsConfig.reduce((count, column) => (column.component ? count + 1 : count), 0);
  }
}
